// umlaut-check: skip — "AuffuellungModal" ist ein Komponenten-/Dateiname
// (technischer Bezeichner), bleibt ASCII.

import React, {useEffect, useState} from "react";
import {Modal, OverlayTrigger, Spinner, Table, Tooltip} from "react-bootstrap";
import axios from "axios";
import Farbfeld from "./Farbfeld.jsx";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {formatFarbnummer, formatFaktor, formatKw, formatMenge} from "./format.js";
import {friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

/**
 * Auffüllvorschlag für eine Ankunftswoche, deren LKW noch nicht voll ist
 * (Auftrag AP9 Punkt 2, PLAN.md Abschnitt 4.7: "schlägt der Bereich vor,
 * womit er sinnvoll aufgefüllt wird"). Zeigt nur an, speichert nichts -
 * kein Knopf zum Übernehmen, bewusst rein informativ.
 */
export default function AuffuellungModal({ankunftKw, onClose}) {
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState(null);

    useEffect(() => {
        if (!ankunftKw) {
            setDaten(null);
            return undefined;
        }
        const controller = new AbortController();
        setLaedt(true);
        setFehler(null);
        axios
            .get(`/cosmos/daten/vorschlaege/auffuellung/${ankunftKw}`, {signal: controller.signal})
            .then(({data}) => setDaten(data))
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                setFehler(friendlyErrorMessage(e, "Der Auffüllvorschlag konnte nicht geladen werden."));
            })
            .finally(() => setLaedt(false));
        return () => controller.abort();
    }, [ankunftKw]);

    return (
        <Modal show={Boolean(ankunftKw)} onHide={onClose} centered size="lg" aria-labelledby="cosmos-auffuellung-title">
            <Modal.Header closeButton>
                <Modal.Title id="cosmos-auffuellung-title" style={{fontSize: 16}}>
                    Auffüllvorschlag {ankunftKw ? formatKw(ankunftKw) : ""}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">
                    Farbtöne, die noch nicht für diese Ankunftswoche geplant sind, sortiert nach kürzester Reichweite und höchstem Basisverbrauch – je
                    Farbton volle Paletten bis zum Zielbestand, solange der LKW noch Kapazität hat. Dieser Vorschlag wird nur angezeigt, nicht
                    gespeichert und löst nichts aus.
                </p>

                {laedt && (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" size="sm" role="status">
                            <span className="visually-hidden">Wird geladen…</span>
                        </Spinner>
                    </div>
                )}

                {fehler && <p className="text-danger">{fehler}</p>}

                {daten && !laedt && (
                    <>
                        <p className="small">
                            Belegt: {formatMenge(daten.bereits_belegt)} von {formatMenge(daten.kapazitaet)} Dosen · Noch frei:{" "}
                            {formatMenge(daten.rest_kapazitaet)} Dosen
                        </p>

                        {daten.vorschlaege.length === 0 ? (
                            <p className="text-muted">Für diese Woche ergibt sich kein sinnvoller Auffüllvorschlag (keine volle Palette mehr möglich, oder alle Farbtöne stehen bereits auf Zielbestand).</p>
                        ) : (
                            <Table size="sm" responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th />
                                        <th>
                                            <HeaderWithHelp help="Farbton mit Artikelnummer.">Artikel</HeaderWithHelp>
                                        </th>
                                        <th>
                                            <HeaderWithHelp help="Vorgeschlagene Auffüllmenge in Stück.">Menge</HeaderWithHelp>
                                        </th>
                                        <th>
                                            <HeaderWithHelp help="Vorgeschlagene Auffüllmenge, umgerechnet auf volle Paletten.">Paletten</HeaderWithHelp>
                                        </th>
                                        <th>
                                            <HeaderWithHelp help="Warum genau dieser Farbton und diese Menge vorgeschlagen werden.">Begründung</HeaderWithHelp>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {daten.vorschlaege.map((v) => (
                                        <tr key={v.artikel_nummer}>
                                            <td>
                                                <Farbfeld farbwert={v.farbwert} />
                                            </td>
                                            <td>
                                                {v.farbnummer !== null && v.farbnummer !== undefined ? `${formatFarbnummer(v.farbnummer)} ` : ""}
                                                {v.farbname || v.artikel_nummer}
                                                <div className="small text-muted">{v.artikel_nummer}</div>
                                            </td>
                                            <td>{formatMenge(v.menge)}</td>
                                            <td>{formatFaktor(v.paletten, 1)}</td>
                                            <td className="small text-muted">{v.begruendung}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="auffuellung-schliessen-tip">Schließt dieses Fenster. Der Auffüllvorschlag ist rein informativ und bestellt nichts.</Tooltip>}>
                    <span>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
                            Schließen
                        </button>
                    </span>
                </OverlayTrigger>
            </Modal.Footer>
        </Modal>
    );
}
